<template>
  <v-layout id="permissions-selection" column align-center>
    <permissions-client-role
      v-if="
        accountType !== 'clientRole' &&
          userHasManagementPermission('client_role', 'list') &&
          canSeeTable &&
          !loading
      "
      :client-roles-from-parent="previousClientRoles"
      :permissions-selected="permissionsSelectedFromParent"
      :previous-roles-and-permissions="selectedPermissions"
      :all-permissions="allPermissions"
      @add-select-permissions="addClientRolesPermissions"
      @remove-select-permissions="removeClientRolesPermissions"
      @update-client-roles="$emit('selected-client-roles', $event)"
      :disabled="!hasPermissionToEdit"
    />

    <v-expansion-panels
      v-model="openedExpansionPanel"
      v-if="!loading && canSeeTable"
      multiple
      class="elevation-3 expansion-panel-permission"
    >
      <v-expansion-panel
        v-for="role in relevantRoles"
        :key="role.name"
        v-if="role.show"
      >
        <v-expansion-panel-header>
          <role-item
            :role-name="role.name"
            :disabled="!hasPermissionToEdit || userRole === 'viewer'"
            :permissions-selected="selectedPermissions[role.name]"
            @updatePermissions="updateSelection(role.name, $event)"
          />
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-card style="padding-left: 50px; box-shadow: none">
            <permissions-list
              :role="role.name"
              :user-role="userRole"
              :disabled="!hasPermissionToEdit"
              :selected-permissions-from-parent="selectedPermissions[role.name]"
              @update-selection="updateSelection(role.name, $event)"
            />
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <app-no-permission-warning v-if="!canSeeTable" />

    <div v-if="loading" style="height: 100px; width: 100%; position: relative">
      <loading-spinner
        :active.sync="loading"
        :is-full-page="false"
        color="#4fa2b7"
      />
    </div>
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import RoleItem from "./RoleItem";
import PermissionsList from "@/components/SystemManagement/Accounts/Permissions/PermissionsList";
import LoadingSpinner from "vue-loading-overlay";
import LockSVG from "@/assets/management/lock.svg";
import AppNoPermissionWarning from "@/components/_Molecules/AppNoPermissionWarning";
import PermissionsClientRole from "@/components/SystemManagement/Accounts/Permissions/PermissionsClientRole";

export default {
  name: "Permissions",

  components: {
    PermissionsClientRole,
    AppNoPermissionWarning,
    PermissionsList,
    RoleItem,
    LoadingSpinner,
    LockSVG
  },

  mixins: [checkPermissionsMixin],

  props: {
    /**
     * Determine which parent page is using
     * @example "company", "web", "mobile", "clientRole"
     */
    accountType: {
      type: String,
      required: true
    },

    userRole: {
      type: String,
      required: false
    },

    /**
     * If the account type is company, it receives the company type
     */
    companyType: Number,

    /**
     * Receives the permissions grouped by role of the selected user
     */
    previousRolesAndPermissions: Array,

    /**
     * Determines whether the user has permission to edit permissions
     */
    hasPermissionToEdit: Boolean,

    /**
     * Receives the permissions of the selected user
     */
    permissionsSelectedFromParent: Array,

    /**
     * Receives the permissions grouped by client roles of the selected user
     */
    previousClientRoles: Array,

    permissionsSelectedByRole: {
      type: Object
    },

    /** 
     * The action the user is performing. Can be 'create' or 'edit'
     */
    actionType: {
      type: String
    }
  },

  data() {
    return {
      /**
       * Determines open expansion panels
       */
      openedExpansionPanel: null,

      /**
       * Store selected permissions grouped by role
       */
      selectedPermissions: {
        management: [],
        fibercloud_web: [],
        fibercloud_mobile: [],
        modules: [],
        network: []
      },

      /**
       * Determines which role is selected
       */
      selectedRole: null,

      /**
       * All system permissions
       */
      allPermissions: null,
    };
  },

  computed: {
    /**
     * Define if the page is loading
     */
    loading() {
      return this.$store.getters.areAllRolesAndPermissionsLoading;
    },

    /**
     * Returns the relevant roles
     * @returns {[{name: string, show: boolean}, {name: string, show: *}, {name: string, show: boolean}, {name: string, show: boolean}]}
     */
    relevantRoles() {
      return [
        {
          name: "management",
          show:
            (this.accountType === "company" && this.companyType !== 3) ||
            this.accountType === "web" || this.accountType === "mobile" ||
            this.accountType === "clientRole"
        },
        { name: "fibercloud_web", show: this.canHaveFibercloudWeb || this.accountType === "mobile"},
        { name: "fibercloud_mobile", show: !this.accountType === "mobile" },
        { name: "modules", show: true },
        {
          name: "network",
          show:
            (this.accountType === "company" && this.companyType !== 3) ||
            this.accountType === "web" || this.accountType === "mobile" ||
            this.accountType === "clientRole"
        }
      ];
    },

    /**
     * Returns the roles being displayed
     * @returns {*[]}
     */
    rolesInUse() {
      let rolesInUse = [];
      for (let role of this.relevantRoles) {
        if (role.show) rolesInUse.push(role.name);
      }
      return rolesInUse;
    },

    /**
     * Returns the selected web permissions
     * @returns {*[]}
     */
    webPermissionsSelected() {
      let permissionsSelected = this.selectedPermissions.fibercloud_web.concat(
        this.selectedPermissions.modules,
        this.selectedPermissions.network
      );

      if (this.companyType !== 3) {
        return permissionsSelected.concat(this.selectedPermissions.management);
      } else {
        return permissionsSelected;
      }
    },

    /**
     * Returns the selected mobile permissions
     * @returns {*[]}
     */
    mobilePermissionsSelected() {
      let permissionsSelected = this.selectedPermissions.fibercloud_web.concat(
        this.selectedPermissions.modules,
        this.selectedPermissions.network
      );

      if (this.companyType !== 3) {
        return permissionsSelected.concat(this.selectedPermissions.management);
      } else {
        return permissionsSelected;
      }
      // return this.selectedPermissions.fibercloud_mobile.concat(
      //   this.selectedPermissions.modules
      // );
    },

    /**
     * Returns the selected permissions
     * @returns {*[]}
     */
    permissionsSelected() {
      if (this.canHaveFibercloudWeb) {
        return this.webPermissionsSelected;
      } else if (this.accountType === "mobile") {
        return this.mobilePermissionsSelected;
      }
    },

    /**
     * Determines whether FibercloudWeb tab can be accessed
     * @returns {boolean}
     */
    canHaveFibercloudWeb() {
      return (
        this.accountType === "company" ||
        this.accountType === "web" ||
        this.accountType === "clientRole"
      );
    },

    /**
     * Determines if the user can list the items.
     * @return {boolean}
     */
    canSeeTable() {
      let enabled = this.userHasManagementPermission("permission_list");
      if (this.accountType === "company") {
        return (
          enabled && this.userHasManagementPermission("company_permission_list")
        );
      } else if (this.accountType === "web" || this.accountType === "mobile") {
        return (
          enabled && this.userHasManagementPermission("user_permission_list")
        );
      } else if (this.accountType === "clientRole") {
        return (
          enabled &&
          this.userHasManagementPermission("client_role_permission_list")
        );
      }
    },

    /**
     * Indicates if the user is creating a new Viewer user and if its permissions are not yet set
     * 
     * @return {Boolean}
     */
    userIsCreatingNewViewerWithoutPermissions() {
      return this.userRole === 'viewer' && this.actionType === 'create';
    },
  },

  mounted() {
    this.getAllPermissions();

    this.$nextTick(() => {
      this.FETCH_ALL_ROLES_PERMISSIONS_LIST();
  
      if (this.previousRolesAndPermissions) {
        this.getPreviousPermissions();
      }
      this.openedExpansionPanel = [0, 1, 2, 3];
      if (this.permissionsSelectedByRole) {
        this.selectedPermissions = this.permissionsSelectedByRole;
      }
    })
  },

  watch: {
    /**
     * Whenever the value of the accountType variable is changed, the selectedRole variable is null
     */
    accountType() {
      this.selectedRole = null;
    },

    /**
     * Whenever the value of the permissionsSelected variable is changed,
     * the data of this change is sent to the parent component
     * @param newValue
     */
    permissionsSelected(value) {
      this.$emit("selectedPermissions", value);
    },

    "selectedPermissions.fibercloud_mobile"() {
      this.$emit("updateSelectedPermissionsByRole", this.selectedPermissions);
    },

    "selectedPermissions.fibercloud_web"() {
      this.$emit("updateSelectedPermissionsByRole", this.selectedPermissions);
    },

    "selectedPermissions.management"() {
      this.$emit("updateSelectedPermissionsByRole", this.selectedPermissions);
    },

    "selectedPermissions.network"() {
      this.$emit("updateSelectedPermissionsByRole", this.selectedPermissions);
    },

    "selectedPermissions.modules"() {
      this.$emit("updateSelectedPermissionsByRole", this.selectedPermissions);
    },

    /**
     * Whenever the value of the rolesInUser variable is changed,
     * the selectedRole and selectedRole variables are null
     */
    rolesInUser() {
      this.openedExpansionPanel = this.selectedRole = null;
    },

    /**
     * Whenever the value of the openedExpansionPanel variable is changed and the value is valid,
     * it changes the value of the selectedRole variable
     * @param newValue
     */
    openedExpansionPanel(newValue) {
      if (newValue != null) {
        this.selectedRole = this.rolesInUse[newValue];
      }
    },

    /**
     * Whenever the value of the previousRolesAndPermissions variable is changed,
     * the getPreviousPermissions method is called, sending the new value
     */
    previousRolesAndPermissions() {
      this.getPreviousPermissions();
    },
    
    /**
     * When the system permissions are loaded and if the user is creating a new Viewer user with no set permissions, 
     * the method to load the viewer base permissions is loaded
     * @param {Array} value - the newly loaded system permissions
     */
    allPermissions(value) {
      if (value.length > 0) {
        this.checkIfPermissionsCanBeSet();
      }
    },

    /**
     * When the user role changes the method to check if the permissions can be set is called
     */
    userRole(value) {
      if (value === 'viewer') {
        this.setViewerPermissions();
      }
    }
  },

  methods: {
    ...mapActions(["FETCH_ALL_ROLES_PERMISSIONS_LIST", "fetchAllPermissions"]),

    /**
     * Update permissions selection
     * @param role Which role to update
     * @param updatedPermissionList - Updated list of permissions
     */
    updateSelection(role, updatedPermissionList) {
      this.selectedPermissions[role] = updatedPermissionList;
    },

    /**
     * Get previous permissions
     */
    getPreviousPermissions() {
      for (let roleInUser of this.relevantRoles) {
        let role = this.previousRolesAndPermissions.find(
          i => i.name === roleInUser.name
        );
        if (role) {
          let rolePermissions = role.permissions;
          for (let i = 0; i < rolePermissions.length; i++) {
            this.selectedPermissions[role.name].push(rolePermissions[i]["id"]);
          }
        }
      }
    },

    /**
     * When adding a client role, the associated permissions are selected
     * @param clientRole
     */
    addClientRolesPermissions(clientRole) {
      for (let [key, values] of Object.entries(clientRole)) {
        for (let value of values) {
          this.selectedPermissions[key].push(value);
        }
      }
    },

    /**
     * When removing a client role, the associated permissions are deselected
     * @param clientRole
     */
    removeClientRolesPermissions(clientRole) {
      for (let [key, values] of Object.entries(clientRole)) {
        for (let value of values) {
          this.selectedPermissions[key].splice(
            this.selectedPermissions[key].indexOf(value),
            1
          );
        }
      }
    },

    /**
     * Loads all the system permissions
     */
    async getAllPermissions() {
      try {
        this.allPermissions = await this.fetchAllPermissions("roles");
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * If the user is creating a new Viewer with unset permissions the method to set the permissions is called
     */
    checkIfPermissionsCanBeSet() {
      if (this.userIsCreatingNewViewerWithoutPermissions) {
        this.setViewerPermissions();
      }
    },

    /**
     * Sets the viewer base permissions. These are all the show, list, list_all, list_objects and modules permissions
     */
    setViewerPermissions() {
      try {
        let permissionsByRoles = {
          management: [],
          fibercloud_web: [],
          fibercloud_mobile: [],
          modules: [],
          network: []
        }
        let acceptedPermissionTypes = ['show', 'list', 'list_all', 'list_objects'];
        let acceptedPermissionNames = ['geoserver', 'object_forms']
        
        this.allPermissions.forEach(permission => {
          if (acceptedPermissionTypes.includes(permission.action_type) || permission.type === 'module' || acceptedPermissionNames.includes(permission.name)) {
            permission.roles.forEach(role => {
              permissionsByRoles[role.name].push(permission.id)
            });
          }
        });
        this.selectedPermissions = permissionsByRoles;
      } catch (e) {
        console.error(e)
      } finally {
        this.$toasted.global.toast_success(this.$i18n.t("ViewerPermissionsSet"));
      }
    }
  }
};
</script>

<style scoped>
.expansion-panel-permission {
  width: 99%;
  height: 99%;
}
</style>

<style>
#permissions-selection .v-expansion-panel-header {
  min-height: unset;
  padding: 6px 24px;
}
.expansion-panel-permission .v-expansion-panel {
  border-radius: 0 !important;
  margin: 0;
}
.expansion-panel-permission .v-expansion-panels {
  z-index: unset;
}
</style>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/Permissions/Permissions.vue